import { useCallback, useMemo, useState } from 'react';
import {
  TableCell as MuiTableCell,
  Collapse,
  useTheme,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import ConfirmDialog from '../../common/Confirm.component';
import { fetchAPI } from '../../../api/request';
import Loading from '../../common/Loading.component';
import SimpleTable from '../../simpleTable/SimpleTable.component';
import Button from '../../common/Button.component';
import { InfoOutlined } from '@mui/icons-material';
import Alert from '../../common/Alert.component';
import { CHECK_ITEMS_MAGAZZINO } from '../../../module/listItem/conf/Data.conf';
import TableBodyItemsEditable from './components/TableBodyItemsEditable.component';

const TableItemsLogbookEditable = ({
  open,
  data,
  idLogbook,
  orderById = 'createdAt',
  orderType = 'desc',
  handleSuccess = () => {},
  statusLogbook,
  module,
  header,
  checked = {},
  onCheckedChange = () => {},
}) => {
  const theme = useTheme();
  const { query } = module;
  const { jwt } = useSelector((state) => state.login);

  const [orderBy, setOrderBy] = useState(orderById);
  const [order, setOrder] = useState(orderType);
  const [currentState, setCurrentState] = useState(null);
  const [editable, setEditable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);

  const disabledButtonCheck = useMemo(
    () => data?.filter((item) => item?.attributes?.stock_available === false),
    [data]
  );
  const disabledButtonRemove = useMemo(
    () => data?.filter((item) => item?.attributes?.stock_available === true),
    [data]
  );

  //Sort products rows for each logbook
  const sortedData = useMemo(
    () => _.orderBy(data, [orderBy], [order]),
    [orderBy, order, data]
  );
  const numSelected = useMemo(
    () => _.keys(_.pickBy(checked, _.identity))?.length,
    [checked]
  );

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const onChange = (value, name, id) => {
    setCurrentState({
      ...currentState,
      [name]: { ...currentState?.[name], [id]: value ? value : null },
    });
  };

  // eslint-disable-next-line
  const onSuccess = (item) => {
    setLoading(false);
    setEditable({ ...editable, [item?.id]: false });
    handleSuccess();
  };

  const onSelectItem = ({ id }) => {
    onCheckedChange({
      ...checked,
      [id]: !checked?.[id],
    });
  };

  const onSelectAll = () => {
    const isChecked =
      (data?.length > 0 && data?.length === numSelected) || false;
    onCheckedChange(
      data?.reduce((acc, item) => {
        acc[item?.id] = !isChecked;
        return acc;
      }, {})
    );
  };

  const handleUpdateStock = useCallback(
    (body) => {
      setSuccess(false);
      fetchAPI(
        query.action.settings,
        {},
        '',
        {
          ids: _.keys(_.pickBy(checked, Boolean)),
          ...body,
        },
        jwt
      )
        .then(() => {
          setSuccess(true);
          onSuccess();
        })
        .catch((err) => setError(err));
    },
    [checked, query, jwt, onSuccess]
  );

  return (
    <MuiTableCell
      style={{ paddingBottom: 0, paddingTop: 0, width: '100%' }}
      colSpan={7}
    >
      <Collapse in={open || false} timeout='auto' unmountOnExit>
        {loading && <Loading />}
        {success && <Alert severity='success'>{'Success!!'}</Alert>}

        {error && (
          <Alert severity='error'>
            {error?.response?.data?.error?.message || 'An error has occurred'}
          </Alert>
        )}
        <Stack>
          <Stack
            mt={2}
            mb={2}
            spacing={3}
            direction={{ xs: 'column', sm: 'row' }}
          >
            {numSelected !== 0 && (
              <Stack mb={2} direction={'row'} alignItems={'center'} spacing={1}>
                <Button
                  variant='contained'
                  color='success'
                  onClick={() => setOpenConfirm(true)}
                  disabled={_.isEmpty(disabledButtonCheck)}
                >
                  Check Items
                </Button>
                <Tooltip
                  title={`Once the selected items are verified, their status changes to ${CHECK_ITEMS_MAGAZZINO}`}
                >
                  <span>
                    <IconButton
                      disabled={_.isEmpty(disabledButtonCheck)}
                      color='success'
                    >
                      <InfoOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}

            {numSelected !== 0 && (
              <Stack mb={2} direction={'row'} alignItems={'center'} spacing={1}>
                <Button
                  variant='contained'
                  color='error'
                  onClick={() => setOpenRemove(true)}
                  disabled={_.isEmpty(disabledButtonRemove)}
                >
                  Remove Stock
                </Button>
                <Tooltip
                  title={`The selected items will be removed from stock`}
                >
                  <span>
                    <IconButton
                      color='error'
                      disabled={_.isEmpty(disabledButtonRemove)}
                    >
                      <InfoOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <SimpleTable
            module={{ list: { conf: { columns: header } } }}
            maxHeight={'100%'}
            numSelected={numSelected}
            onSort={handleSort}
            order={order}
            orderBy={orderBy}
            backgroundColor={theme.palette.secondary.medium_20}
            colorText={theme.palette.primary.dark}
            stickyHeader={false}
            onSelectAll={() => onSelectAll(idLogbook, sortedData)}
            isChecked={data?.length > 0 && data?.length === numSelected}
            data={sortedData}
            body={
              <TableBodyItemsEditable
                data={sortedData}
                module={module}
                onChange={onChange}
                checked={checked}
                onSelectItem={onSelectItem}
                isSelectable={true}
                setSuccessEdit={handleSuccess}
                hasAvailable
                hasStock
                statusLogbook={statusLogbook}
                moveDelete
              />
            }
          />

          <ConfirmDialog
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
            title='Are you sure you want to confirm all selected items?'
            description='The selected items change status to READY TO BE DELIVERED and their presence in the STOCK is confirmed. You cannot undo this action.'
            cancelAction={() => setOpenConfirm(false)}
            confirmAction={() =>
              handleUpdateStock({
                available: CHECK_ITEMS_MAGAZZINO,
                stock_available: true,
              })
            }
            label={'Confirm'}
            color='info'
          />

          <ConfirmDialog
            open={openRemove}
            onClose={() => setOpenRemove(false)}
            title='Are you sure you want to remove from STOCK all selected items?'
            description='The selected items will be removed from STOCK. You cannot undo this action.'
            cancelAction={() => setOpenRemove(false)}
            confirmAction={() =>
              handleUpdateStock({
                stock_available: false,
              })
            }
            label={'Confirm'}
            color='info'
          />
        </Stack>
      </Collapse>
    </MuiTableCell>
  );
};

export default TableItemsLogbookEditable;
